/**
 * denominationalReasoningEngine.js
 * Determines which denominational knowledge sources Kevia needs for a given message.
 *
 * Returns null when no denominational knowledge is needed (most messages).
 * Returns a DenominationalPlan when EGW or Church Manual context would add value.
 *
 * The plan is sent to the backend alongside bibleQuery so the stream route
 * can run the appropriate retrieval pipelines.
 *
 * @typedef {{
 *   questionType:    string,
 *   sourceTypes:     string[],
 *   tone:            string,
 *   citationDensity: string,
 *   maxCitations:    number,
 * }} DenominationalPlan
 */

import { QUESTION_TYPE_SOURCES } from "./sourceHierarchy";

// ── Pattern sets (conservative — only fire when confident) ──────────

const EGW_EXPLICIT_RE = /elena\s*(?:g\.?\s*)?white|egw|el\s+deseado\s+de\s+todas|camino\s+a\s+cristo|obreros\s+evang[eé]licos|ministerio\s+pastoral\s+de\s+egw|consejos\s+para\s+la\s+iglesia|eventos\s+de\s+los\s+[uú]ltimos|sra\.?\s*white/i;

const CHURCH_MANUAL_EXPLICIT_RE = /manual\s+de\s+iglesia|disciplina\s+eclesial|proceso\s+disciplinario|junta\s+de\s+iglesia\b|carta\s+de\s+traslado|voto\s+(?:de\s+)?iglesia|membresía.*proceso|proceso.*membresía|c[oó]mo\s+(?:se\s+)?disciplina|reglamento\s+(?:de\s+)?iglesia/i;

const PASTORAL_COUNSEL_PATTERNS = [
  /consejería\s+pastoral/i,
  /orientaci[oó]n\s+espiritual/i,
  /matrimonio\s+en\s+crisis/i,
  /familia\s+en\s+crisis/i,
  /c[oó]mo\s+(?:puedo\s+)?(?:aconsejar|orientar|guiar|ayudar)\s+(?:a\s+)?(?:una\s+)?(?:familia|persona|matrimonio|pareja)/i,
  /situaci[oó]n\s+(?:muy\s+)?dif[íi]cil/i,
  /conflicto\s+(?:familiar|matrimonial|conyugal)/i,
  /adicci[oó]n/i,
  /depresi[oó]n\s+espiritual/i,
  /alejado\s+de\s+(?:dios|la\s+iglesia)/i,
  /restauraci[oó]n\s+espiritual/i,
  /duelo\s+espiritual/i,
];

const DEVOTIONAL_PATTERNS = [
  /vida\s+espiritual\s+(?:de|del|de\s+la)/i,
  /crecimiento\s+espiritual/i,
  /comuni[oó]n\s+(?:con\s+dios|diaria)/i,
  /devocional\s+(?:personal|para)/i,
  /meditaci[oó]n\s+(?:espiritual|b[íi]blica)/i,
  /deseo\s+(?:de\s+)?acercarme\s+(?:m[aá]s\s+)?a\s+dios/i,
];

const SERMON_EGW_PATTERNS = [
  /cita\s+(?:de\s+)?elena\s+white/i,
  /referencia\s+(?:de\s+)?egw/i,
  /incluir\s+(?:algo\s+de\s+)?elena/i,
  /egw\s+(?:sobre|acerca|en)/i,
  /que\s+dice\s+elena/i,
  /serm[oó]n.*egw|egw.*serm[oó]n/i,
];

// ── QuestionType detection ───────────────────────────────────────────

function detectQuestionType(text, intent) {
  // 1. Explicit EGW mention (highest signal)
  if (EGW_EXPLICIT_RE.test(text)) {
    if (SERMON_EGW_PATTERNS.some((p) => p.test(text)) || intent === "sermon" || intent === "reflection") {
      return "sermon_support";
    }
    return "pastoral";
  }

  // 2. Explicit Church Manual mention
  if (CHURCH_MANUAL_EXPLICIT_RE.test(text)) {
    return "administrative";
  }

  // 3. Intent-based: sermon/reflection → offer EGW support
  if (intent === "sermon" || intent === "reflection") {
    // Only activate if there's a clear devotional/spiritual angle
    if (DEVOTIONAL_PATTERNS.some((p) => p.test(text))) return "devotional";
    // Sermons often benefit from EGW — activate with lower bar
    return "sermon_support";
  }

  // 4. Pastoral counsel patterns
  if (PASTORAL_COUNSEL_PATTERNS.some((p) => p.test(text))) {
    return "pastoral";
  }

  // 5. Devotional patterns
  if (DEVOTIONAL_PATTERNS.some((p) => p.test(text))) {
    return "devotional";
  }

  return "none";
}

// ── Tone mapping ─────────────────────────────────────────────────────

const TONE_MAP = {
  administrative: "administrative_pastoral",
  pastoral:       "pastoral",
  devotional:     "devotional",
  sermon_support: "homiletical",
  doctrinal:      "doctrinal",
  none:           "pastoral",
};

// ── Public API ────────────────────────────────────────────────────────

/**
 * Build a denominational source plan for a user message.
 *
 * @param {string} text   — raw user message
 * @param {string} intent — pre-detected intent from orchestrateKevia
 * @returns {DenominationalPlan | null}
 */
export function buildDenominationalPlan(text, intent) {
  if (!text?.trim()) return null;

  const questionType = detectQuestionType(text, intent);
  if (questionType === "none") return null;

  const { sourceTypes, citationDensity } = QUESTION_TYPE_SOURCES[questionType] ?? QUESTION_TYPE_SOURCES.none;
  if (!sourceTypes || sourceTypes.length === 0) return null;

  const maxCitations = citationDensity === "medium" ? 2 : 1;

  return {
    questionType,
    sourceTypes,
    tone:            TONE_MAP[questionType] ?? "pastoral",
    citationDensity,
    maxCitations,
  };
}

/**
 * Returns the system prompt addendum to inject when a denominational plan is active.
 * Kept short — appended to the existing system prompt.
 *
 * @param {DenominationalPlan} plan
 * @returns {string}
 */
export function buildDenominationalAddendum(plan) {
  if (!plan) return "";

  const citationRule = plan.citationDensity === "none"
    ? "No incluyas citas denominacionales."
    : plan.citationDensity === "medium"
      ? `Puedes incluir hasta ${plan.maxCitations} citas con atribución completa (libro, capítulo).`
      : "Incluye 1 referencia breve máximo con atribución (libro, capítulo).";

  const toneGuide = {
    administrative_pastoral: "Responde con claridad administrativa y calidez pastoral.",
    pastoral:    "Responde pastoralmente. Sé cálido, práctico, no dogmático.",
    devotional:  "Responde con profundidad espiritual y tono contemplativo.",
    homiletical: "Apoya al predicador con material homilético contextualizado.",
    doctrinal:   "Mantén equilibrio doctrinal adventista.",
  }[plan.tone] ?? "";

  const sourceLine = plan.sourceTypes.includes("church_manual")
    ? "Si usas el Manual de Iglesia, cita la sección específica."
    : plan.sourceTypes.includes("egw")
      ? "Si usas Elena White, cita libro y capítulo. No parafrasees sin atribuir."
      : "";

  return `\n\nCONTEXTO DENOMINACIONAL ADVENTISTA: ${toneGuide} ${citationRule} ${sourceLine}`.trim();
}
